import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TranslocoService } from '@jsverse/transloco';
import { DecimalPipe } from '@angular/common';
import { QAStatsOverviewDay } from '../../../../core/interfaces/admin-interfaces';

@Component({
  selector: 'app-qa-overview-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './qa-overview-chart.component.html',
  styleUrl: './qa-overview-chart.component.css',
})
export class QaOverviewChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;

  @Input() overviewData: QAStatsOverviewDay[] = [];

  v1Label: string = 'V1';
  v2Label: string = 'V2';

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) {}

  ngOnInit(): void {
    this.updateChart();

    this.translocoService.selectTranslate('admin.qa.v1_accuracy').subscribe((label) => {
      this.v1Label = label;
      this.updateChart();
    });

    this.translocoService.selectTranslate('admin.qa.v2_accuracy').subscribe((label) => {
      this.v2Label = label;
      this.updateChart();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();
  }

  updateChart(): void {
    Chart.register(ChartDataLabels);

    const labels = this.overviewData.map(d => d.date);
    const accuracyOld = this.overviewData.map(d => d.accuracy_old);
    const accuracyNew = this.overviewData.map(d => d.accuracy_new);

    this.data = {
      labels: labels,
      datasets: [
        {
          label: this.v1Label,
          data: accuracyOld,
          borderColor: '#E57373',
          backgroundColor: '#E5737333',
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          pointStyle: 'circle',
          pointRadius: 5,
          pointBackgroundColor: '#E57373',
        },
        {
          label: this.v2Label,
          data: accuracyNew,
          borderColor: '#81C784',
          backgroundColor: '#81C78433',
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          pointStyle: 'circle',
          pointRadius: 5,
          pointBackgroundColor: '#81C784',
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            font: {
              family: 'Gilroy',
              size: 12,
            },
          },
        },
        datalabels: {
          align: 'end',
          anchor: 'end',
          clamp: true,
          clip: false,
          display: 'auto',
          formatter: (value: any) => {
            return `${this.decimalPipe.transform(value, '1.1-1')}%`;
          },
          color: '#000000',
          font: {
            size: 11,
            weight: 'normal',
            family: 'Gilroy',
          },
        },
        tooltip: {
          callbacks: {
            label: (tooltipItem: any) => {
              const label = tooltipItem.dataset.label || '';
              const value = tooltipItem.raw || 0;
              const dayData = this.overviewData[tooltipItem.dataIndex];
              const fileCount = dayData?.file_count ?? 0;
              return `${label}: ${this.decimalPipe.transform(value, '1.1-1')}% (${fileCount} files)`;
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: false,
          },
          ticks: {
            font: {
              family: 'Gilroy',
              size: 11,
            },
          },
        },
        y: {
          display: true,
          beginAtZero: true,
          max: 100,
          ticks: {
            callback: (value: any) => `${value}%`,
            font: {
              family: 'Gilroy',
              size: 11,
            },
          },
          grid: {
            color: '#E0E0E0',
          },
        },
      },
    };
  }
}
